import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { warehouseAPI, productionAPI } from '../api';
import SearchSelect from '../components/SearchSelect';
import { confirmToast } from '../components/ConfirmToast';
import { makeMatcher } from '../utils/search';
import toast from 'react-hot-toast';
import { openCard, openDivider } from '../components/openCard';

// ─── Замены: какие листы в цехе считают одним и тем же ───────────────────────
//
// Один лист приходит от разных поставщиков под разными именами, и в справочнике
// 1С это разные позиции. 16 мм 2750×1830 живёт под пятнадцатью именами; реальный
// остаток лежит на восьми. Кончилась одна — в цехе берут вторую, не спрашивая.
//
// Рецепт же хранил ОДНО имя. Пока на нём есть остаток, всё сходится; кончилось —
// списание уходит в минус при живой плите на складе, и правит это бухгалтер.
//
// Экран отвечает на два вопроса и больше ни на что:
//   • что система возьмёт, если этой позиции не станет;
//   • где рецепт всё ещё держится за одно имя — и это скоро выстрелит.

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 12, padding: 16,
};

const num = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

const btn = (kind) => ({
  background: kind === 'main' ? 'var(--accent)' : 'var(--bg)',
  border: '1px solid ' + (kind === 'main' ? 'var(--accent)' : 'var(--border)'),
  borderRadius: 8, color: kind === 'main' ? '#fff' : 'var(--muted)',
  padding: '8px 16px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
});

// ─── Одна группа ─────────────────────────────────────────────────────────────
function GroupCard({ group, noms, marks, onChanged }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);

  const g = group;
  const inGroup = new Set((g.items || []).map(i => String(i.nomenclature)));
  const free = noms.filter(n => n.category === g.category && !inGroup.has(String(n.id)));

  const add = async (id) => {
    if (!id) return;
    setBusy(true);
    try {
      const { data } = await warehouseAPI.addToGroup(g.id, { nomenclature: id });
      setAdding('');
      if (data.recipes_moved > 0) {
        toast.success(`${t('Добавлено. Рецептов переведено на группу')}: ${data.recipes_moved}`);
      } else {
        toast.success(t('Добавлено'));
      }
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || t('Не удалось добавить'));
    } finally { setBusy(false); }
  };

  const setVariant = async (item, value) => {
    try {
      await warehouseAPI.updateGroupItem(g.id, item.id, { variant: value });
      onChanged();
    } catch { toast.error(t('Не удалось сохранить признак')); }
  };

  const drop = async (item) => {
    const ok = await confirmToast(
      `${t('Убрать из группы')} «${item.name}»?\n\n`
      + t('Позиция останется в справочнике и на складе — она просто перестанет '
          + 'подставляться вместо других.'),
      { danger: true, confirmText: t('Убрать') });
    if (!ok) return;
    try {
      await warehouseAPI.removeGroupItem(g.id, item.id);
      onChanged();
    } catch { toast.error(t('Не удалось убрать')); }
  };

  // Что уйдёт в обычную смену: первая непомеченная позиция с остатком.
  // Это главный ответ экрана, поэтому он виден без раскрытия группы.
  const first = (g.items || [])
    .filter(i => !i.variant && i.quantity > 0)
    .sort((a, b) => a.priority - b.priority || b.quantity - a.quantity)[0];

  const line = g.is_reject ? '#94a3b8' : (g.total > 0 ? '#22c55e' : '#ef4444');

  return (
    <div style={{
      ...CARD,
      borderLeft: `3px solid ${line}`,
      opacity: g.is_reject ? 0.75 : 1,
      // Раскрытая группа отходит от соседей: без этого её плиты читаются
      // как начало следующей группы, а подставится совсем другая
      ...openCard(open, line),
    }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14.5 }}>
            {g.name}
            {g.is_reject && (
              <span style={{
                marginLeft: 8, background: '#94a3b8', color: '#fff', borderRadius: 5,
                padding: '1px 7px', fontSize: 10.5, fontWeight: 700,
              }}>{t('НЕ ИДЁТ В ПРОИЗВОДСТВО')}</span>
            )}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 3 }}>
            {(g.items || []).length} {t('поз.')}
            {g.recipes > 0 && ` · ${t('в рецептах')}: ${g.recipes}`}
            {!g.is_reject && first && ` · ${t('возьмёт')}: ${first.short_name || first.name}`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            color: g.total > 0 ? 'var(--text)' : '#ef4444', fontWeight: 800, fontSize: 16,
          }}>{num(g.total)}</div>
          <div style={{ color: 'var(--dim)', fontSize: 11 }}>{t('всего')}</div>
        </div>
        <span style={{ color: 'var(--dim)', fontSize: 12, width: 12 }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ ...openDivider(line), display: 'flex',
                      flexDirection: 'column', gap: 5 }}>
          {/* Без этой строки поле «обычная» читается как подпись, а не как
              место, куда пишут. Признак — единственное, чем ЧФМК отличается от
              остальных плит: в справочнике его нет ни в названии, ни в коде. */}
          <div style={{
            background: 'var(--bg)', borderRadius: 8, padding: '9px 12px',
            color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5,
          }}>
            <b style={{ color: 'var(--text)' }}>{t('Признак')}</b>{' — '}
            {t('то же слово, которое стоит в заявке в поле «Производитель ДСП» — '
               + 'его называет менеджер при заказе. Поставьте его плите справа: '
               + 'тогда смена с этим словом возьмёт именно её, а обычная смена — '
               + 'не возьмёт никогда. Пусто = обычная плита.')}
          </div>

          {(g.items || []).map(i => (
            <div key={i.id} style={{
              background: 'var(--bg)', borderRadius: 8, padding: '9px 12px',
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ color: 'var(--text)', fontSize: 13, wordBreak: 'break-word' }}>
                  {i.name}
                </div>
                {/* Известные признаки — кнопками: набирать «чфмк» руками
                    значит однажды написать «ЧФМК » с пробелом, и плита
                    молча выпадет из выбора. */}
                {(marks || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                    {marks.map(m => (
                      <button key={m}
                        onClick={() => setVariant(i, i.variant === m ? '' : m)}
                        style={{
                          background: i.variant === m ? '#f59e0b' : 'var(--card)',
                          border: '1px solid ' + (i.variant === m ? '#f59e0b' : 'var(--border)'),
                          borderRadius: 6, color: i.variant === m ? '#fff' : 'var(--muted)',
                          padding: '3px 9px', fontSize: 11.5, fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>{m}</button>
                    ))}
                  </div>
                )}
              </div>
              <input
                key={i.variant}
                defaultValue={i.variant}
                onBlur={e => e.target.value !== i.variant && setVariant(i, e.target.value)}
                placeholder={t('обычная')}
                title={t('Признак: чфмк, влагостойка. Пусто — обычная плита')}
                style={{
                  width: 120, background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 6, color: i.variant ? '#f59e0b' : 'var(--text)',
                  padding: '5px 8px', fontSize: 12, fontFamily: 'inherit',
                }} />
              <div style={{
                width: 78, textAlign: 'right', fontWeight: 700, fontSize: 13.5,
                color: i.quantity > 0 ? 'var(--text)' : 'var(--dim)',
              }}>{num(i.quantity)}</div>
              <button onClick={() => drop(i)} title={t('Убрать из группы')} style={{
                background: 'none', border: 'none', color: '#ef4444',
                cursor: 'pointer', fontSize: 14, padding: '0 4px',
              }}>✕</button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
            <SearchSelect
              options={free.map(n => ({ id: n.id, name: n.name }))}
              value={adding}
              onChange={(v) => { setAdding(v); add(v); }}
              placeholder={busy ? t('Добавляю…') : t('+ добавить позицию в группу')}
              disabled={busy}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Страница ────────────────────────────────────────────────────────────────
export default function SubstitutesPage() {
  const [groups, setGroups]   = useState([]);
  const [problems, setProb]   = useState(null);
  const [noms, setNoms]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [showReject, setShowReject] = useState(false);
  const [building, setBuilding] = useState(false);
  // Слова берём оттуда же, откуда они и приходят: из подсказок поля «Размер
  // плиты». Придумывать свой список значит однажды разойтись с цехом.
  const [marks, setMarks] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      warehouseAPI.getGroups(),
      warehouseAPI.groupProblems(),
      warehouseAPI.getNomenclatures(),
      productionAPI.getSuggestions('plate_size').catch(() => ({ data: {} })),
    ])
      .then(([g, p, n, sug]) => {
        const fromShop = ((sug.data || {}).results || [])
          .map(x => (x.value || '').trim().toLowerCase()).filter(Boolean);
        const used = (g.data.results || g.data || [])
          .flatMap(x => (x.items || []).map(i => i.variant)).filter(Boolean);
        setMarks(Array.from(new Set([...fromShop, ...used])).slice(0, 8));
        setGroups(g.data.results || g.data || []);
        setProb(p.data);
        setNoms((n.data.results || n.data || [])
          .filter(x => x.category === 'dsp' || x.category === 'paper'));
      })
      .catch(() => toast.error(t('Не удалось загрузить группы')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const autobuild = async () => {
    const { data } = await warehouseAPI.previewAutobuild({ category: 'dsp,paper' });
    const ok = await confirmToast(
      `${t('Собрать группы по справочнику')}?\n\n`
      + `${t('Получится групп')}: ${data.groups}.\n`
      + t('Плиты собираются по толщине и размеру, плёнка — по декору. Уже '
          + 'собранные группы не задваиваются, позиции только добавляются. '
          + 'Рецепты сразу переводятся на группы.'),
      { confirmText: t('Собрать') });
    if (!ok) return;
    setBuilding(true);
    try {
      const { data: res } = await warehouseAPI.runAutobuild({ category: 'dsp,paper' });
      toast.success(
        `${t('Групп создано')}: ${res.created} · ${t('позиций')}: ${res.items_added}`
        + (res.recipes_moved ? ` · ${t('рецептов переведено')}: ${res.recipes_moved}` : ''));
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || t('Не удалось собрать'));
    } finally { setBuilding(false); }
  };

  const fix = async (row) => {
    if (!row.suggest) return;
    // Позиция с признаком заменой не становится: МДФ вместо шлифованной плиты
    // подставлять нельзя, и рецепт на неё останется как был. Говорим это до
    // нажатия, а не после — иначе человек ждёт, что минус исчезнет.
    const ok = await confirmToast(
      row.variant
        ? `${t('Добавить')} «${row.name}»\n${t('в группу')} «${row.suggest.name}»`
          + ` ${t('с признаком')} «${row.variant}»?\n\n`
          + t('Это другой материал, а не другое имя того же: обычная смена его '
              + 'не возьмёт, и рецепт останется на нём. Позиция просто будет '
              + 'видна в группе.')
        : `${t('Добавить')} «${row.name}»\n${t('в группу')} «${row.suggest.name}»?\n\n`
          + `${t('Рецептов, которые этим чинятся')}: ${row.recipes}. `
          + t('После этого смена возьмёт со склада ту позицию, которая есть.'),
      { confirmText: t('Добавить') });
    if (!ok) return;
    try {
      const { data } = await warehouseAPI.addToGroup(row.suggest.id, { nomenclature: row.id });
      toast.success(`${t('Готово. Рецептов переведено')}: ${data.recipes_moved}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || t('Не удалось добавить'));
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;

  const needle = q.trim().toLowerCase();
  // Кусками и вразбивку: «дсп 22 чфмк» должно находить группу, даже если
  // эти слова стоят в названии в другом порядке или в разных плитах.
  const m = makeMatcher(needle);
  const hit = (g) => m(g.name) || (g.items || []).some(i => m(i.name));

  const work = groups.filter(g => !g.is_reject && hit(g));
  const rej  = groups.filter(g => g.is_reject && hit(g));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 830 }}>
        {t('Один и тот же лист приходит от разных поставщиков под разными именами, и в '
          + 'справочнике это разные позиции. Группа говорит системе: это одно и то же — '
          + 'кончилась одна, бери следующую. Пока рецепт держится за одно имя, смена '
          + 'уходит в минус при живом остатке на складе.')}
      </div>

      {/* Рецепты, которые ещё держатся за одно имя с нулевым остатком */}
      {problems && problems.count > 0 && (
        <div style={{
          background: '#ef444414', border: '1px solid #ef444440',
          borderRadius: 12, padding: 16,
        }}>
          <div style={{ color: '#ef4444', fontWeight: 800, fontSize: 15 }}>
            {problems.count} {t('позиций в рецептах — с нулевым остатком')}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
            {t('Каждая закрытая по такому рецепту смена уходит в минус: система списывает '
              + 'именно это имя, а лежит на складе другое. Добавьте позицию в группу — '
              + 'рецепты перейдут на неё сами.')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {problems.results.map(r => (
              <div key={r.id} style={{
                background: 'var(--card)', borderRadius: 8, padding: '9px 12px',
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                  <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 2 }}>
                    {t('в рецептах')}: {r.recipes}
                    {r.suggest ? ` · ${t('похоже на')} «${r.suggest.name}»` : ` · ${t('подходящей группы нет')}`}
                    {r.variant && (
                      <span style={{ color: '#f59e0b' }}>
                        {' · '}{t('это')} {r.variant} — {t('заменой не станет')}
                      </span>
                    )}
                  </div>
                </div>
                {r.suggest ? (
                  <button onClick={() => fix(r)} style={btn('main')}>
                    {t('В группу')} «{r.suggest.name}» →
                  </button>
                ) : (
                  <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                    {t('добавьте вручную ниже')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Признак из заявки, который ни к чему не привязан. Смена спишет
          обычную плиту — молча, потому что «чфмк» для системы просто слово. */}
      {problems && (problems.variants || []).length > 0 && (
        <div style={{
          background: '#f59e0b14', border: '1px solid #f59e0b40',
          borderRadius: 12, padding: 16,
        }}>
          <div style={{ color: '#f59e0b', fontWeight: 800, fontSize: 15 }}>
            {t('Признак из заявки ни к чему не привязан')}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
            {t('Технолог пишет это в «Размере плиты», но в группах ни одна плита так '
              + 'не помечена — смена возьмёт обычную. Найдите нужную плиту ниже и '
              + 'напишите ей этот же признак: у одинаковых плит имена разные, и '
              + 'угадать за вас система не может.')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {problems.variants.map(v => (
              <span key={v.variant} style={{
                background: 'var(--card)', border: '1px solid #f59e0b40',
                borderRadius: 8, padding: '7px 12px', fontSize: 13, color: 'var(--text)',
              }}>
                <b style={{ color: '#f59e0b' }}>{v.variant}</b>
                <span style={{ color: 'var(--dim)' }}> · {t('заданий')}: {v.jobs}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={autobuild} disabled={building} style={btn('main')}>
          {building ? t('Собираю…') : t('⚙ Собрать по справочнику')}
        </button>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Поиск по группе или позиции…')}
          style={{
            flex: '1 1 240px', minWidth: 200, background: 'var(--bg)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
            padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
          }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {work.length === 0 ? (
          <div style={{ ...CARD, textAlign: 'center', padding: '32px 20px', color: 'var(--muted)', fontSize: 13 }}>
            {needle ? t('Ничего не найдено')
              : t('Групп пока нет. Нажмите «Собрать по справочнику» — система разберёт '
                  + 'названия и предложит готовые.')}
          </div>
        ) : work.map(g => (
          <GroupCard key={g.id} group={g} noms={noms} marks={marks} onChanged={load} />
        ))}
      </div>

      {/* Некондиция — своей полкой: числится ради остатка, в смену не идёт */}
      {rej.length > 0 && (
        <>
          <button onClick={() => setShowReject(v => !v)} style={{
            ...btn(), alignSelf: 'flex-start',
          }}>
            {showReject ? '▾' : '▸'} {t('Некондиция')} · {rej.length}
          </button>
          {showReject && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ color: 'var(--dim)', fontSize: 12.5, lineHeight: 1.5, maxWidth: 800 }}>
                {t('Эти листы числятся ради остатка и в производство не идут. В смену '
                  + 'система их не возьмёт ни при каких условиях — потому они и вынесены '
                  + 'отдельно, а не поставлены последними в очередь.')}
              </div>
              {rej.map(g => (
                <GroupCard key={g.id} group={g} noms={noms} marks={marks} onChanged={load} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
